import { apiService } from '../utils/apiService';
import { API_CONFIG } from '../utils/constants';

export const ACTIVE_EVENT_STATUS_IDS = [
  0, 1, 2, 3, 4, 5, 8, 9, 10, 18, 19, 20, 30, 40, 50, 60, 70, 80, 90
];

const ensureArray = (value) => Array.isArray(value) ? value : [];

const normalizeCustomFieldValues = (data) => ({
  ids: {
    eventTypeId: data?.ids?.eventTypeId ?? null,
    eventInvolvementId: data?.ids?.eventInvolvementId ?? null,
    eventLocationId: data?.ids?.eventLocationId ?? null,
    plannedDate: data?.ids?.plannedDate ?? null
  },
  display: data?.display ?? {},
  customFields: Array.isArray(data?.customFields) ? data.customFields : []
});

const appendNumberArray = (searchParams, key, values) => {
  if (!Array.isArray(values) || values.length === 0) return;
  values
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
    .forEach((value) => searchParams.append(key, String(value)));
};

const appendString = (searchParams, key, value) => {
  if (typeof value !== 'string' || value.trim() === '') return;
  searchParams.set(key, value.trim());
};

const paramsSerializer = (params) => {
  const searchParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== '') {
          searchParams.append(key, item);
        }
      });
      return;
    }

    searchParams.append(key, value);
  });

  return searchParams.toString();
};

const getEnvironmentType = () => {
  const urlToCheck = `${API_CONFIG.BASE_URL || ''} ${window.location.href || ''}`.toLowerCase();
  if (urlToCheck.includes('staging') || urlToCheck.includes('stage')) return 'Staging';
  if (urlToCheck.includes('uat')) return 'UAT';
  if (urlToCheck.includes('dev') || urlToCheck.includes('development')) return 'Development';
  if (urlToCheck.includes('localhost') || urlToCheck.includes('127.0.0.1')) return 'Development';
  return 'Live';
};

const normalizeActiveEvent = (item) => {
  if (!item || typeof item !== 'object') return item;

  const id = item.id;

  return {
    ...item,
    id,
    breakdownId: id,
    reference: item.breakdownReference,
    companyName: item.customerName,
    followUpTimeMin: Number(item.followUpTimeMin ?? 0)
  };
};

const normalizeEventDetail = (breakdown) => {
  if (!breakdown || typeof breakdown !== 'object') return breakdown;

  return {
    ...breakdown,
    breakdownId: breakdown.id,
    reference: breakdown.breakdownReference || breakdown.reference,
    customerName: breakdown.customerName || breakdown.companyName,
    category: breakdown.breakdownCategory
  };
};

export const normalizeEvent = normalizeActiveEvent;

const buildChangeLogPayload = (details, breakdownId, userName) => ({
  address: details?.address ?? null,
  breakdownId: breakdownId ?? null,
  driver: details?.driver ?? null,
  driverNumber: details?.driverNumber ?? null,
  initiatorName: details?.initiatorName ?? null,
  initiatorNumber: details?.initiatorNumber ?? null,
  controllerName: details?.controllerName ?? null,
  controllerNumber: details?.controllerNumber ?? null,
  caseNumber: details?.caseNumber ?? null,
  category: details?.category ?? null,
  subCategory: details?.subCategory ?? null,
  description: details?.description ?? null,
  cause: details?.cause ?? null,
  correction: details?.correction ?? null,
  vehicleLoad: details?.vehicleLoad ?? null,
  vehicleFleet: details?.vehicleFleet ?? null,
  vehicleRegistration: details?.vehicleRegistration ?? null,
  vehicleVINNumber: details?.vehicleVINNumber ?? null,
  vehicleMake: details?.vehicleMake ?? null,
  vehicleModel: details?.vehicleModel ?? null,
  vehicleYear: details?.vehicleYear ?? null,
  vehicleWarranty: details?.vehicleWarranty ?? null,
  trailerFleet: details?.trailerFleet ?? null,
  trailerRegistration: details?.trailerRegistration ?? null,
  trailerVINNumber: details?.trailerVINNumber ?? null,
  trailerMake: details?.trailerMake ?? null,
  trailerModel: details?.trailerModel ?? null,
  trailerYear: details?.trailerYear ?? null,
  loggedInUserId: userName ?? null,
  breakdownType: details?.breakdownType ?? null,
  handoverNotes: details?.handoverNotes ?? null,
  dateUpdated: new Date().toISOString(),
  breakdownStatusId: details?.breakdownStatusId ?? null,
  breakdownStatus: '',
  imageCount: 0
});

const eventService = {
  normalizeEvent,

  createEvent: async (payload) => apiService.post(API_CONFIG.EVENTS.CREATE, payload, { timeout: 0 }),

  getOems: async () => ensureArray(await apiService.get(API_CONFIG.OEM.GET_ALL)),

  getCompanies: async () => ensureArray(await apiService.get(API_CONFIG.COMPANY.GET_COMPANY_LIST)),

  getCompanyDrivers: async (companyId) => {
    if (!companyId) return [];
    const response = await apiService.get(`${API_CONFIG.DRIVERS.GET_COMPANY_DRIVERS}/${companyId}`);
    return ensureArray(response?.drivers ?? response);
  },

  searchCompanyVehicles: async (companyId, searchTerm = '') => {
    if (!companyId) return [];

    const term = String(searchTerm || '').trim();
    const fields = term ? ['registration', 'fleet', 'vin'] : [null];
    const responses = await Promise.all(fields.map((field) => {
      const config = field ? { params: { [field]: term } } : {};
      return apiService.get(`${API_CONFIG.VEHICLES.GET_COMPANY_VEHICLES}/${companyId}`, config);
    }));

    const vehicles = responses.flatMap((response) => ensureArray(response?.data ?? response));
    const seen = new Set();
    return vehicles.filter((vehicle) => {
      const id = vehicle?.id;
      if (!id || seen.has(String(id))) return false;
      seen.add(String(id));
      return true;
    });
  },

  getFinanceCurrencies: async () =>
    ensureArray(await apiService.get(API_CONFIG.SUPPLIER.GET_FINANCE_CURRENCIES)),

  getTelematicsLocation: async (vehicle) => apiService.post(
    API_CONFIG.TELEMATICS.GET_VEHICLE_LOCATION,
    {
      vehicleId: vehicle?.id || '',
      vehicleVin: vehicle?.vin || '',
      vehicleRegistration: vehicle?.registration || '',
      vehicleFleet: vehicle?.fleet || ''
    }
  ),

  autocompleteAddress: async (address) => {
    if (!String(address || '').trim()) return [];
    const response = await apiService.get(API_CONFIG.GOOGLEGEOCODE.AUTOCOMPLETE, {
      params: { address: String(address).trim() }
    });
    return ensureArray(response?.suggestions);
  },

  geocodePlace: async (placeId) => {
    if (!placeId) return null;
    const response = await apiService.get(API_CONFIG.GOOGLEGEOCODE.GEOCODE_BY_PLACE_ID, {
      params: { placeId }
    });
    return ensureArray(response?.results)[0] || null;
  },

  reverseGeocode: async (latitude, longitude) => {
    const response = await apiService.get(API_CONFIG.GOOGLEGEOCODE.REVERSE_GEOCODE, {
      params: { latitude, longitude }
    });
    return ensureArray(response?.results)[0] || null;
  },

  getActiveEvents: async ({
    page = 1,
    pageSize = 1000,
    searchTerm,
    statuses = ACTIVE_EVENT_STATUS_IDS,
    eventTypeIds = [],
    eventInvolvementFilters = [],
    eventLocationIds = [],
    eventStatusIds = [],
    eventProviderStatusIds = []
  } = {}) => {
    const params = {};
    const searchParams = new URLSearchParams();

    if (page) searchParams.set('page', String(page));
    if (pageSize) searchParams.set('pageSize', String(pageSize));
    appendString(searchParams, 'searchTerm', searchTerm);
    appendNumberArray(searchParams, 'statuses', statuses);
    appendNumberArray(searchParams, 'eventTypeIds', eventTypeIds);
    appendNumberArray(searchParams, 'eventLocationIds', eventLocationIds);
    appendNumberArray(searchParams, 'eventStatusIds', eventStatusIds);
    appendNumberArray(searchParams, 'eventProviderStatusIds', eventProviderStatusIds);
    if (Array.isArray(eventInvolvementFilters) && eventInvolvementFilters.length > 0) {
      searchParams.set('eventInvolvementFilters', JSON.stringify(eventInvolvementFilters));
    }

    searchParams.forEach((value, key) => {
      if (params[key] === undefined) {
        params[key] = value;
      } else if (Array.isArray(params[key])) {
        params[key].push(value);
      } else {
        params[key] = [params[key], value];
      }
    });

    const response = await apiService.get(API_CONFIG.EVENTS.ACTIVE_LIST, {
      params,
      paramsSerializer
    });
    const items = ensureArray(response?.items).map(normalizeActiveEvent);
    return {
      ...(response && typeof response === 'object' && !Array.isArray(response) ? response : {}),
      items
    };
  },

  getLookups: async () => {
    const response = await apiService.get(API_CONFIG.EVENTS.LOOKUPS);
    return response || {
      eventTypes: [],
      eventInvolvements: [],
      eventLocations: [],
      eventStatuses: [],
      eventProviderStatuses: []
    };
  },

  getEventDetails: async (eventId) => {
    const response = await apiService.get(`${API_CONFIG.EVENTS.GET_FULL_DETAIL}/${eventId}`);
    const breakdown = normalizeEventDetail(response?.breakdown);

    return {
      ...response,
      breakdown,
      notes: ensureArray(response?.notes),
      assignedMechanics: ensureArray(response?.assignedMechanics)
    };
  },

  getEventStatuses: async (eventTypeId) => {
    if (!eventTypeId) return [];
    return ensureArray(await apiService.get(`${API_CONFIG.EVENTS.GET_STATUSES}/${eventTypeId}`));
  },

  getCurrentEventStatus: async (eventId) => {
    if (!eventId) return null;
    return apiService.get(`${API_CONFIG.EVENTS.GET_CURRENT_STATUS}/${eventId}`);
  },

  getMechanicDetails: async (breakdownId) => {
    if (!breakdownId) return [];
    return ensureArray(await apiService.get(`${API_CONFIG.EVENTS.GET_MECHANIC_DETAILS}/${breakdownId}`));
  },

  getCompanyAssignedMechanics: async (breakdownId) => {
    if (!breakdownId) return [];
    const response = await apiService.get(`${API_CONFIG.EVENTS.GET_COMPANY_ASSIGNED_MECHANICS}/${breakdownId}`);
    return ensureArray(response?.mechanics);
  },

  getSupplierMechanics: async () => ensureArray(await apiService.get(API_CONFIG.COMPANY.GET_MECHANIC_LIST)),

  assignMechanicAndSupplier: async (
    breakdownId,
    { mechanicId, mechanicName, supplierId, companyId, companyName }
  ) => apiService.post(
    `${API_CONFIG.EVENTS.ASSIGN_MECHANIC}/${breakdownId}`,
    {},
    {
      params: {
        mechanicId,
        mechanicName,
        supplierId,
        companyId,
        companyName,
        type: 'PWA'
      }
    }
  ),

  acceptMechanic: async ({ breakdownId, breakdownMechanicId, mechanicName, mechanicMobile, latitude, longitude }) =>
    apiService.post(API_CONFIG.BREAKDOWNS.ACCEPT_MECHANIC_STATUS, {
      breakdownId,
      breakdownMechanicId,
      mechanicName,
      mechanicMobile,
      latitude: latitude != null ? String(latitude) : '',
      longitude: longitude != null ? String(longitude) : '',
      type: 'PWA'
    }),

  declineMechanic: async ({ breakdownId, mechanicId, loginId, userName, fullName, companyName }) =>
    apiService.post(`${API_CONFIG.BREAKDOWNS.UPDATE_MECHANIC_STATUS}/${breakdownId}`, '', {
      params: {
        mechanicId,
        status: 1,
        loginId,
        UserId: mechanicId,
        userName,
        fullName,
        companyName,
        type: 'PWA'
      }
    }),

  updateEventStatus: async ({ breakdownId, eventTypeId, eventStatusId, cancellationReason }) => {
    const response = await apiService.post(API_CONFIG.EVENTS.UPDATE_STATUS, {
      breakdownId,
      eventTypeId,
      eventStatusId,
      cancellationReason,
      type: getEnvironmentType()
    });

    if (response?.data) {
      try {
        await apiService.post(API_CONFIG.EVENTS.STATUS_NOTIFICATION, response.data);
      } catch (error) {
        console.warn('Failed to post event status notification', error);
      }
    }

    return response;
  },

  getCancellationReasons: async () => ensureArray(await apiService.get(API_CONFIG.EVENTS.CANCELLATION_REASONS)),

  updateBreakdownMechanicStatus: async ({ breakdownMechanicId, mechanicModelId, status, eta, ata, breakdownId, isMechanic }) =>
    apiService.put(`${API_CONFIG.SUPPLIER.UPDATE_BREAKDOWN_MECHANIC}/${breakdownMechanicId}`, {
      mechanicModelId,
      status,
      eta,
      ata,
      breakdownId,
      isMechanic
    }),

  getEventHistory: async ({ breakdownId, registrationNumber, trailerRegistrationNumber, fleetNumber } = {}) => {
    if (!breakdownId) return [];

    return ensureArray(await apiService.post(API_CONFIG.EVENTS.GET_HISTORY, {
      breakdownId,
      registrationNumber: registrationNumber || '',
      trailerRegistrationNumber: trailerRegistrationNumber || '',
      fleetNumber: fleetNumber || ''
    }));
  },

  fullResolve: async ({ breakdownId, eventTypeId, userName }) => {
    const type = getEnvironmentType();
    const response = await apiService.post(
      `${API_CONFIG.EVENTS.FULL_RESOLVE}/${breakdownId}`,
      {},
      { params: { type, eventTypeId } }
    );
    const details = await apiService.get(`${API_CONFIG.EVENTS.GET_BREAKDOWN_BY_ID}/${breakdownId}`);
    await apiService.post(API_CONFIG.EVENTS.INSERT_CHANGE_LOG, buildChangeLogPayload(details, breakdownId, userName));
    return response;
  },

  completeBreakdown: async (payload) =>
    apiService.post(API_CONFIG.REPAIR_ACTIONS.FINISH_BREAKDOWN, {
      ...payload,
      type: 'PWA'
    }),

  getCustomFields: async (eventTypeId) => {
    if (!eventTypeId) return [];
    return ensureArray(await apiService.get(API_CONFIG.EVENTS.GET_CUSTOM_FIELDS, { params: { eventTypeId } }));
  },

  getCustomFieldValues: async (breakdownId) => {
    if (!breakdownId) return null;
    const data = await apiService.get(`${API_CONFIG.EVENTS.CUSTOM_FIELD_VALUES}/${breakdownId}`);
    return normalizeCustomFieldValues(data);
  },

  updateCustomFieldValues: async (breakdownId, payload) =>
    apiService.put(`${API_CONFIG.EVENTS.CUSTOM_FIELD_VALUES}/${breakdownId}`, payload),

  getMediaIds: async (breakdownId) => {
    if (!breakdownId) return [];
    return ensureArray(await apiService.get(`${API_CONFIG.EVENTS.GET_IMAGE_IDS}/${breakdownId}`));
  },

  getMedia: async (mediaId) => {
    if (!mediaId) return null;
    const media = await apiService.post(API_CONFIG.EVENTS.GET_IMAGE, { Id: mediaId });
    if (!media) return null;

    return {
      id: mediaId,
      base64: media.base64 || '',
      mediaType: (media.mediaType || 'image').toLowerCase()
    };
  },

  addMedia: async (payload) => apiService.post(API_CONFIG.EVENTS.ADD_IMAGE, payload),

  addNote: async ({ breakdownId, note, followUpDate = null }) => {
    const response = await apiService.post(API_CONFIG.BREAKDOWNS.INSERT_NOTES, {
      breakdownId,
      note,
      isFollowUpComplete: false,
      type: getEnvironmentType(),
      followUpDate
    });

    if (response?.data) {
      try {
        await apiService.post(API_CONFIG.EVENTS.NOTE_NOTIFICATION, response.data);
      } catch (error) {
        console.warn('Failed to post event note notification', error);
      }
    }

    return response;
  }
};

export default eventService;
